import { countryList } from './constants'

/**
 * Turns one locality's aggregate row (from /public/GetAirconLocalityStats) into
 * the sentences and figures on its locality page.
 *
 * The 26 locality pages used to differ only in the area name. What makes each
 * one worth indexing is how that area compares with the rest of Singapore, so
 * most sentences here are comparisons with the island-wide row - two areas with
 * different booking patterns get genuinely different text.
 *
 * Wording is tied to what the data means:
 *  - a job counts once it was booked and assigned, whatever happened next, so
 *    the copy says "booked", never "completed";
 *  - jobsBand is floored ("40+") and shares are rounded to 5%, so no sentence
 *    implies more precision than that.
 *
 * Pure function of its inputs - no fetches - so every page renders the same
 * text for the same data.
 */

// Differences smaller than this are rounding noise at 5% granularity.
const NOTABLE_DIFFERENCE = 10

const lower = (label) => label.toLowerCase()

const shareOf = (row, label) => row?.serviceMix?.find((s) => s.label === label)?.share ?? 0

// Differences in typical booking cost smaller than this are not worth a sentence.
const NOTABLE_COST_DIFFERENCE = 5

export const buildLocalityInsights = (row, singapore, locality, windowStart, { isAmongBusiest = false } = {}) => {
    if (!row?.jobsBand) return null

    const since = windowStart ? new Date(windowStart).getFullYear() : null
    const sentences = []

    sentences.push(
        `${row.jobsBand} aircon jobs have been booked in ${locality} through StringsSG${since ? ` since ${since}` : ''}${isAmongBusiest ? `, making it one of the busiest areas for StringsSG aircon technicians` : ''}.`
    )

    // Across areas the top service is almost always general servicing, so the
    // runners-up are what distinguish one area from another.
    const top = row.serviceMix?.[0]
    if (top) {
        const next = row.serviceMix.slice(1, 3).map((s) => `${lower(s.label)} (${s.share}%)`)
        sentences.push(
            `The most-booked service here is ${lower(top.label)}, included in ${top.share}% of bookings${next.length ? `, followed by ${next.join(' and ')}` : ''}.`
        )
    }

    // The single most notable gap against island-wide. A service booked MORE
    // often here says more about the area than one booked less often, so a
    // qualifying positive gap wins over a larger negative one.
    if (singapore?.serviceMix?.length && row.serviceMix?.length) {
        const labels = [...new Set([...row.serviceMix, ...singapore.serviceMix].map((s) => s.label))]
        const gaps = labels.map((label) => ({ label, diff: shareOf(row, label) - shareOf(singapore, label) }))
        const byDiff = (a, b) => Math.abs(b.diff) - Math.abs(a.diff)
        const biggest =
            gaps.filter((g) => g.diff >= NOTABLE_DIFFERENCE).sort(byDiff)[0] ??
            gaps.sort(byDiff)[0]
        if (biggest && Math.abs(biggest.diff) >= NOTABLE_DIFFERENCE) {
            const local = shareOf(row, biggest.label)
            const island = shareOf(singapore, biggest.label)
            const label = biggest.label.charAt(0) + lower(biggest.label).slice(1)
            sentences.push(
                `${label} is booked ${biggest.diff > 0 ? 'more' : 'less'} often in ${locality} than island-wide (${local}% of bookings, against ${island}% across Singapore).`
            )
        } else {
            sentences.push(`The mix of services booked in ${locality} is close to the island-wide pattern.`)
        }
    }

    if (row.medianUnits) {
        const island = singapore?.medianUnits
        const unitWord = row.medianUnits === 1 ? 'unit' : 'units'
        sentences.push(
            island && island !== row.medianUnits
                ? `A typical booking here covers ${row.medianUnits} ${unitWord}, compared with ${island} island-wide.`
                : `A typical booking here covers ${row.medianUnits} ${unitWord}.`
        )
    }

    if (row.ceilingShare >= 10) {
        sentences.push(
            `${row.ceilingShare}% of servicing bookings in ${locality} include a ceiling cassette unit${singapore?.ceilingShare != null ? ` (${singapore.ceilingShare}% island-wide)` : ''}.`
        )
    }

    if (row.medianOneTimeCost) {
        const island = singapore?.medianOneTimeCost
        const diff = island ? row.medianOneTimeCost - island : 0
        const comparison = !island
            ? ''
            : Math.abs(diff) < NOTABLE_COST_DIFFERENCE
                ? `, in line with the island-wide typical of $${island}`
                : `, ${diff > 0 ? 'above' : 'below'} the island-wide typical of $${island}`
        sentences.push(`The typical one-time booking in ${locality} comes to about $${row.medianOneTimeCost}, transport included${comparison}.`)
    }

    if (row.packageShare >= 10) {
        sentences.push(`${row.packageShare}% of customers here chose a multi-visit package rather than a one-off service.`)
    }

    const facts = [
        { label: since ? `Aircon jobs booked since ${since}` : 'Aircon jobs booked', value: row.jobsBand },
        top && { label: 'Most-booked service', value: top.label },
        row.medianUnits && { label: 'Typical units per booking', value: String(row.medianUnits) },
        row.medianOneTimeCost && { label: 'Typical one-time booking', value: `$${row.medianOneTimeCost}` },
    ].filter(Boolean)

    return { sentences, facts, serviceMix: row.serviceMix ?? [] }
}

const SG_LOCALITY_DETAILS = (countryList.find((c) => c.country_cd === 'sg')?.localityListDetails ?? [])
    .filter((l) => l.name !== 'Singapore' && Number.isFinite(l.lat) && Number.isFinite(l.lng))

// Great-circle distance in km; plenty accurate at Singapore's scale.
const distanceKm = (a, b) => {
    const rad = (d) => (d * Math.PI) / 180
    const dLat = rad(b.lat - a.lat)
    const dLng = rad(b.lng - a.lng)
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
    return 2 * 6371 * Math.asin(Math.sqrt(h))
}

/** The nearest other locality pages, by the coordinates in utils/constants.js. */
export const getNearbyLocalities = (locality, count = 3) => {
    const here = SG_LOCALITY_DETAILS.find((l) => l.name === locality)
    if (!here) return []
    return SG_LOCALITY_DETAILS
        .filter((l) => l.name !== locality)
        .map((l) => ({ name: l.name, km: distanceKm(here, l) }))
        .sort((a, b) => a.km - b.km)
        .slice(0, count)
        .map((l) => l.name)
}
